import { api } from '../../../../api';
import type { Id } from '../../../../api/types';
import { memoize } from 'lodash';
import throttle from 'lodash/throttle';
import { WORKFLOW_GRID_SIZE, type setupPlugins } from './plugins';
import type { TaskNode } from './task-node';

type Area = ReturnType<typeof setupPlugins>['area'];
type Editor = ReturnType<typeof setupPlugins>['editor'];

type TaskOpsOptions = {
  area: Area;
  editor: Editor;
  taskNodes: Map<number, TaskNode>;
  connectionMap: Map<number, any>;
  getWorkflowId: () => Id | undefined;
  setSyncing: (value: boolean) => void;
};

const TASK_CLIPBOARD_KIND = 'klarvos/task';

const snapCoordinate = (value: number) =>
  Math.round(value / WORKFLOW_GRID_SIZE) * WORKFLOW_GRID_SIZE;

const snapPosition = (position: { x: number; y: number }) => ({
  x: snapCoordinate(position.x),
  y: snapCoordinate(position.y),
});

export function createTaskOps({
  area,
  editor,
  taskNodes,
  connectionMap,
  getWorkflowId,
  setSyncing,
}: TaskOpsOptions) {
  const sendPositionThrottle = 100;

  const throttledSender = memoize((taskId: number) =>
    throttle(
      (pos: { x: number; y: number }) => {
        const workflowId = getWorkflowId();
        if (!workflowId) return;
        const snapped = snapPosition(pos);
        api.workflows
          .updateTask(workflowId, taskId, { visual: snapped })
          .catch(console.error);
      },
      sendPositionThrottle,
      { leading: false, trailing: true }
    )
  );

  const sendNodePosition = (taskId: number, position: { x: number; y: number }) =>
    throttledSender(taskId)(position);

  const deleteTaskNode = async (node: TaskNode) => {
    const workflowId = getWorkflowId();
    if (!workflowId) return;
    setSyncing(true);
    try {
      await editor.removeNode(node.id);
      const apiId = (node as any).apiId ?? Number(node.id);
      if (apiId) {
        taskNodes.delete(apiId);
        for (const [id, conn] of connectionMap) {
          if (conn.source === node || conn.target === node) {
            connectionMap.delete(id);
          }
        }
        await api.workflows.deleteTask(workflowId, apiId);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSyncing(false);
    }
  };

  const exportTask = async (node: TaskNode) => {
    const data = {
      kind: TASK_CLIPBOARD_KIND,
      task: {
        type: node.taskType,
        config: node.config,
      },
    };
    await navigator.clipboard.writeText(JSON.stringify(data));
  };

  const parseTaskFromClipboard = (
    text: string
  ): { type: string; config?: Record<string, unknown> } | null => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return null;
    }

    if (!parsed || typeof parsed !== 'object') return null;
    const value = parsed as {
      kind?: string;
      task?: { type?: unknown; config?: unknown };
    };

    if (value.kind !== TASK_CLIPBOARD_KIND) return null;
    if (!value.task || typeof value.task.type !== 'string') return null;
    return {
      type: value.task.type,
      config:
        value.task.config && typeof value.task.config === 'object'
          ? (value.task.config as Record<string, unknown>)
          : undefined,
    };
  };

  const pasteTask = async (clipboardText?: string): Promise<boolean> => {
    const workflowId = getWorkflowId();
    if (!workflowId) return false;
    try {
      const text = clipboardText ?? (await navigator.clipboard.readText());
      const payload = parseTaskFromClipboard(text);
      if (!payload) return false;
      const { type, config } = payload;
      const visual = { ...area.area.pointer } as any;
      const task = await api.workflows.createTask(workflowId, {
        type,
        visual,
      });
      if (config && Object.keys(config).length) {
        await api.workflows.updateTask(workflowId, task.id, {
          config,
        });
      }
      return true;
    } catch (e) {
      console.error('Unable to paste', e);
      return false;
    }
  };

  return {
    deleteTaskNode,
    exportTask,
    pasteTask,
    sendNodePosition,
  };
}
